import React, { useMemo } from 'react';
import { analyzeEmailCompliance } from '../EmailCompliance/useEmailComplianceChecker';
import { analyzeDeliverability } from '../TemplateDeliverabilityChecker';

const RANK_COLORS = { 3: '#ef4444', 2: '#f59e0b', 1: '#8b5cf6', 0: '#10b981' };

/**
 * Mini Compliance Badge für den Topic Header
 */
const TopicComplianceBadge = ({ topicData }) => {
  const subjectLines = topicData.subjectLines || (topicData.subjectLine ? [topicData.subjectLine] : []);
  const notes = topicData.topicSpecificNotes || (topicData.topicSpecificNote ? [topicData.topicSpecificNote] : []);
  const extractText = (item) => (typeof item === 'string' ? item : item?.text || '');
  const fullText = `${subjectLines.map(extractText).join('\n')}\n${notes.map(extractText).join('\n')}`;
  
  // Sync on purpose: the delayed hook paints an empty row, then the chips
  // arrive and the card grows. The list should be complete on first paint.
  const { complianceStatus, stats } = useMemo(
    () => analyzeEmailCompliance(fullText),
    [fullText]
  );

  // Deliverability / Spam-Score aus derselben Engine wie der Deliverability Check.
  // typicalEmailSpam = Spam-Wörter in einer typisch versendeten E-Mail (Ø pro
  // Variante), damit sich 10 Varianten mit je 1 Spam-Wort nicht auf 10 addieren.
  const deliverability = analyzeDeliverability(notes, subjectLines);
  const delivScore = deliverability ? deliverability.score : null;
  const typicalEmailSpam = deliverability ? deliverability.typicalEmailSpam : 0;

  if (!fullText.trim()) {
    return null;
  }

  const totalIssues = stats.totalViolations + stats.spamRisk;

  // Severity-Ränge: 3 = kritisch/rot, 2 = Warnung/gelb, 1 = Spam-Signal, 0 = sauber.
  const complianceRank = stats.highRisk > 0 ? 3 : stats.mediumRisk > 0 ? 2 : 0;
  const delivRank = delivScore == null ? 0 : delivScore < 4 ? 3 : delivScore < 7 ? 2 : 0;
  const spamRank = (stats.spamRisk > 0 || typicalEmailSpam >= 2) ? 1 : 0;
  const finalRank = Math.max(complianceRank, delivRank, spamRank);

  // Wenn Compliance den Rang (mit-)treibt, deren Label/Farbe zeigen –
  // sonst die Deliverability-/Spam-Story sichtbar machen.
  const complianceDriven = complianceRank === finalRank && finalRank > 0;
  const badgeColor = complianceDriven ? complianceStatus.color : RANK_COLORS[finalRank];
  const badgeLabel = complianceDriven || finalRank === 0 ? complianceStatus.label : 'Spam Risk';

  const showDelivChip = delivScore != null && (delivScore < 7 || typicalEmailSpam > 0);
  // Higher score = better, so a good score must render green — not red.
  const delivChipClass = delivRank === 3 ? 'quick-stat-red' : delivRank === 2 ? 'quick-stat-yellow' : 'quick-stat-green';
  // Plain-language verdict so it's obvious whether it's good or bad.
  const delivWord = delivRank === 3 ? 'Likely Spam' : delivRank === 2 ? 'Slightly Spammy' : 'No Spam';
  const hasStats = totalIssues > 0 || showDelivChip;

  return (
    <div 
      className="topics-compliance-badge-mini"
      title={`${complianceStatus.message}${delivScore != null ? ` · Deliverability: ${delivScore}/10` : ''}`}
    >
      <span 
        className="mini-status-indicator"
        style={{ 
          backgroundColor: badgeColor,
          border: `2px solid ${badgeColor}20`
        }}
      />
      <span className="mini-label">
        <span className="mini-label-title">{badgeLabel}</span>
        {hasStats && (
          <div className="compliance-quick-stats">
            {stats.highRisk > 0 && (
              <span className="quick-stat quick-stat-red">
                <span className="stat-dot" />
                {stats.highRisk} critical
              </span>
            )}
            {stats.mediumRisk > 0 && (
              <span className="quick-stat quick-stat-yellow">
                <span className="stat-dot" />
                {stats.mediumRisk} warning{stats.mediumRisk > 1 ? 's' : ''}
              </span>
            )}
            {stats.spamRisk > 0 && (
              <span className="quick-stat quick-stat-spam">
                <span className="stat-dot" />
                {stats.spamRisk} spam
              </span>
            )}
            {showDelivChip && (
              <span className={`quick-stat ${delivChipClass}`}>
                <span className="stat-dot" />
                {delivWord}
              </span>
            )}
          </div>
        )}
      </span>
    </div>
  );
};

export default TopicComplianceBadge;

